import { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import './Form.css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ImageUpload from '../ImageUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import Spinner from '../Spinner';
import { capitalizeFirstLetter } from '../../utils/functions';
import { SuccessToast, ErrorToast } from '../Toast/toast';
import { useCategory } from '../../CategoryContext';
import { auth, storage } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const CategoryForm = ({ functionality, category, fields, tab = '' }) => {
  const { updateCount } = useCategory();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { vertical_id, id } = useParams();
  const [formData, setFormData] = useState({ vertical_id: vertical_id });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);

  const user = auth.currentUser;

  useEffect(() => {
    document.title =
      capitalizeFirstLetter(functionality) +
      ' ' +
      capitalizeFirstLetter(category) +
      ' - The Simple Platform';

    if (functionality === 'edit') {
      const getItem = async () => {
        const item = await fetchItem();
        setFormData(item);
        setLoading(false);
      };
      getItem();
    } else {
      setLoading(false);
    }
  }, []);

  const buttonStyle = {
    textTransform: 'none',
    margin: '10px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: '30%',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    let newItem = {
      ...formData,
    };

    if (selectedFile) {
      const storageRef = ref(storage, selectedFile.name);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      try {
        // Wait for upload to finish and get the download URL
        const downloadURL = await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log('Upload is ' + progress + '% done');
            },
            (error) => {
              console.error('Upload failed', error);
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });

        // Add the image URL to the newItem object
        newItem['image_url'] = downloadURL;
        console.log('File uploaded successfully:', downloadURL);
      } catch (error) {
        console.error('Error uploading file:', error);
        setSubmitting(false);
        return;
      }
    } else if (selectedFile === '') {
      newItem['image_url'] = '';
    }

    if (functionality === 'add') {
      const [response, _id] = await addItem(newItem);
      if (response === 200) {
        if (category === 'person') updateCount('people', 1);
        else updateCount(category + 's', 1);

        SuccessToast({
          category: category,
          link: true,
          title: formData.title || formData.name || formData.OpportunityTitle,
          _id: _id,
        });
        return navigate(
          location.pathname.slice(0, location.pathname.lastIndexOf('/')) +
            `?source=category_form${tab ? `&tab=${tab}` : ''}`,
          {
            state: { animation: true, new_card_id: _id },
          }
        );
      } else {
        ErrorToast({ category: category });
      }
    } else {
      const response = await editItem(newItem);
      if (response === 200) {
        SuccessToast({ category: category, functionality: 'edit' });
        return navigate(
          location.pathname.slice(0, location.pathname.lastIndexOf('/edit')) +
            `?source=category_form${tab ? `&tab=${tab}` : ''}`,
          {
            replace: true,
          }
        );
      } else {
        ErrorToast({ category: category, functionality: 'edit' });
      }
    }

    setSubmitting(false);
  };

  const addItem = async (item) => {
    const token = await user.getIdToken();
    let url;
    if (category === 'grant') {
      url = process.env.REACT_APP_FLASK_WEBSERVER + 'add_grant_manually';
    } else {
      url = process.env.REACT_APP_FLASK_WEBSERVER + 'add_' + category;
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(item),
    });
    const data = await res.json();
    return [res.status, data._id];
  };

  const editItem = async (item) => {
    const token = await user.getIdToken();
    let url;

    if (category === 'user') {
      url =
        process.env.REACT_APP_FLASK_WEBSERVER +
        'edit_user_by_id?id=' +
        item._id +
        '&vertical_id=' +
        vertical_id;
    } else {
      url =
        process.env.REACT_APP_FLASK_WEBSERVER +
        'edit_' +
        category +
        '?id=' +
        item._id +
        '&vertical_id=' +
        vertical_id;
    }

    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(item),
    });
    await res.json();
    return res.status;
  };

  const fetchItem = async () => {
    const token = await user.getIdToken();

    const url =
      process.env.REACT_APP_FLASK_WEBSERVER +
      'get_' +
      category +
      '_details?id=' +
      id +
      '&vertical_id=' +
      vertical_id;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json();
    return data.data;
  };

  return loading ? (
    <Spinner />
  ) : (
    <form onSubmit={submitForm} className="add-form">
      <h2 className="heading-style">
        {capitalizeFirstLetter(functionality)} {capitalizeFirstLetter(category)}
      </h2>
      {fields.map((field) => (
        <div key={field.name} style={{ textAlign: 'left' }}>
          <label className="label-style">
            {field.label}
            {field.required && <span style={{ color: 'red' }}>&nbsp;*</span>}
          </label>
          {field.drop_down === true ? (
            <select
              id={field.name}
              name={field.name}
              className="input-style"
              value={formData[field.name] || ''}
              onChange={handleChange}
              required={field.required}
              disabled={field.disabled || false}
            >
              <option value="" disabled>
                {field.placeholder || 'Select an option'}
              </option>
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : field.type === 'image' ? (
            <ImageUpload
              existingImageUrl={formData[field.name] || ''}
              onFileSelect={handleFileSelect}
              previewShape={
                category === 'organization' ||
                category === 'job' ||
                category === 'event' ||
                category === 'grant'
                  ? 'square'
                  : 'circle'
              }
            />
          ) : !field.text_area ? (
            <input
              type={field.type}
              id={field.name}
              name={field.name}
              className="input-style"
              placeholder={field.placeholder || ''}
              pattern={field.pattern ? field.pattern : undefined}
              title={field.title ? field.title : undefined}
              required={field.required}
              value={formData[field.name] || ''}
              onChange={handleChange}
              disabled={field.disabled || false}
            />
          ) : (
            <textarea
              type={field.type}
              id={field.name}
              name={field.name}
              className="input-style"
              rows={field.rows || '4'}
              required={field.required}
              value={formData[field.name] || ''}
              placeholder={field.placeholder || ''}
              onChange={handleChange}
            />
          )}
        </div>
      ))}
      {!submitting ? (
        <div>
          <Button type="submit" variant="outlined" disableRipple sx={buttonStyle}>
            {capitalizeFirstLetter(functionality)} {capitalizeFirstLetter(category)}
          </Button>
          <Button
            type="button"
            variant="outlined"
            disableRipple
            onClick={() => {
              navigate(
                location.pathname.slice(0, location.pathname.lastIndexOf(`/${functionality}`)) +
                  `?source=category_form${tab ? `&tab=${tab}` : ''}`,
                {
                  replace: true,
                }
              );
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="2x"
          style={{ marginTop: '20px' }}
        ></FontAwesomeIcon>
      )}
    </form>
  );
};
export default CategoryForm;
